import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form, Image, Table } from "react-bootstrap";
import { api_url } from '../api/config/config';
import { Facility, BillDataStructure, BillStructure, SupplierPlanStructure } from "../types/type";

interface OpportunityStructure {
    id: number,
    due: number,
    close_date: number,
    company_id: number,
    name: string,
    industry: string,
    bid_id: number,
    status: number,
    upload_file_path: string,
    upload_file_name: string
}

interface SummaryTableHeader {
    label: string;
    key: string;
}

interface SummaryTableRow {
    usageType: string;
    [key: string]: string;
}

interface SummaryTable {
    headers: SummaryTableHeader[];
    data: SummaryTableRow[];
}

const DetailBill: React.FC<{ facility: Facility, opportunity: OpportunityStructure, isOpen: boolean }> = ({ facility, opportunity, isOpen }) => {
    const backendURL = api_url;
    const [summaryTable, setSummaryTable] = useState<SummaryTable>();
    const [bills, setBills] = useState<BillStructure[]>();
    const [summaryCsv, setSummaryCsv] = useState<string>();

    const cachedBillsRef = useRef<{ [key: string]: BillStructure[] }>({});
    const [extraHighVoltage, setExtraHighVoltage] = useState(false);
    const [meteredRate, setMeteredRateVoltage] = useState(false);

    useEffect(() => {
        setExtraHighVoltage(facility.category === 1);  // 高圧
        setMeteredRateVoltage(facility.category === 4); // 従量電灯
    }, [facility.category]);

    const getInitialTable = (): SummaryTable => ({
        headers: [{ label: "使用量内訳", key: "usageType" }],
        data: [
            { usageType: "使用量電力ラベル" },
            { usageType: "使用量電力(kWh)" },
            { usageType: meteredRate ? "受電容量(kVA)" : "契約電力(kw)" },
            { usageType: "負荷率(%)" },
            { usageType: "力率(%)" }
        ]
    });

    const fetchBill = useCallback(async () => {
        const cacheKey = `${facility.id}-${opportunity.id}`;

        const response = await fetch(`${backendURL}/getBill?facility_id=${facility.id}&opportunity_id=${opportunity.id}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });


        const res: any = await response.json();

        const summaryTable = getInitialTable();
        const bills: any = Object.values(res.data);

        bills.forEach((bill: any) => {
            const key = `${bill.year}-${bill.month}`;

            summaryTable.headers.push({ label: key, key: key });
            summaryTable.data[1][key] = bill.total_base_usage;
            summaryTable.data[2][key] = bill.contract_usage;
            summaryTable.data[3][key] = bill.load_factor_p;
            summaryTable.data[4][key] = bill.power_factor_p;

            let planUsages: string[] = [];
            let planLabels: string[] = [];

            Object.entries(bill.supplier_plan).forEach(([planKey, planName]) => {
                if (planName) {
                    planLabels.push(planName as string);
                    planUsages.push(bill[planKey]);
                }
            });

            planUsages.push(bill.total_base_usage);
            planLabels.push('合計');
        });

        setSummaryTable(summaryTable);
        setBills(bills);
        cachedBillsRef.current[cacheKey] = bills;
    }, [backendURL, facility.id, opportunity.id, meteredRate]);

    useEffect(() => {
        fetchBill();
    }, [facility.id, opportunity.id, meteredRate]);

    const generateCsvContent = useCallback(() => {
        if (bills) {
            const summary: string[] = [];

            const r1: string[] = ['使用量内訳'];

            bills.forEach(b => {
                const supplierPlans = b.supplier_plan;

                const monthYear = `${b.year}年${b.month}月`;
                r1.push(monthYear);
            });
            summary.push(r1.join(','));

            r1.length = 0;
            r1.push('使用量電力(kWh)');
            bills.forEach(b => {
                r1.push(b.total_base_usage.toString());
            });
            summary.push(r1.join(','));

            r1.length = 0;
            r1.push(meteredRate ? '受電容量(kVA)' : '契約電力(kw)');

            bills.forEach((bill, index) => {
                r1.push(facility.contract_usage.toString());
            });

            summary.push(r1.join(','));

            r1.length = 0;
            r1.push('負荷率(%)');

            bills.forEach((bill, index) => {
                const loadFactor = Number(bill.load_factor_p).toFixed(1);
                r1.push(loadFactor.toString());
            });

            summary.push(r1.join(','));

            if (!['LOW_LOAD_FIXED', 'LOW_LOAD_STEP', 'LOW_LOAD_TIME'].includes(facility.contract_type)) {
                r1.length = 0;
                r1.push('力率(%)');

                bills.forEach((bill, index) => {
                    const powerFactor = Number(bill.power_factor_p).toFixed(1);
                    r1.push(powerFactor.toString());
                });
                summary.push(r1.join(','));
            }

            const summaryCsv = summary.join("\n");

            setSummaryCsv(summaryCsv);
        }
    }, [bills, facility.contract_type, facility.contract_usage, meteredRate]);

    useEffect(() => {
        generateCsvContent();
    }, [generateCsvContent]);

    const downloadCsv = (filename: string, content: string) => {
        const BOM = "\uFEFF";  // UTF-8 BOM
        const blob = new Blob([BOM + content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
    };

    const handleDownload = () => {
        if (summaryCsv) {
            downloadCsv('summary.csv', summaryCsv);
        }
    };

    const handleUsageFileDownload = async (filePath: string, fileName: string) => {
        try {
            const response = await fetch(filePath);
            const blob = await response.blob();

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("ファイルのダウンロードに失敗しました:", error);
        }
    };

    return (
        <>
            <div className={`p-4 view action-class-wrapper ${isOpen ? 'open' : ''}`} id={`action-class-75`}>
                <div className="mb-lg-4 mb-3">
                    {summaryTable && (
                        <div className="table-responsive padding-td-5 td-min-w-60" style={{ overflowX: 'auto' }}>
                            <Form.Control type="hidden" id={`table-summary-csv-75`} />
                            <Table className="table table-bordered table-vertical-align-middle text-center" id="table-summary-<?php e($rec->id); ?>">
                                <thead>
                                    <tr className="fw-300">
                                        {summaryTable.headers.map((header, index) => {
                                            const [year, month] = index !== 0 ? header.label.split('-') : [null, null];
                                            return (
                                                <td key={index} scope="col" className="border-0" style={{ minWidth: "120px" }}>
                                                    {index === 0 ? (
                                                        <>
                                                            <Image className="height-20 mb-1 me-2" src="assets/images/other/icon-pie-chart-graph-with-details.svg" />
                                                            <br />
                                                            {header.label}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="small fw-300">{year}年</span>
                                                            <br />
                                                            {month}月
                                                        </>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {summaryTable.data.slice(1).map((row, rowIndex) => (
                                        <tr key={rowIndex}>
                                            <td className="bg-ass-table fw-300 border-right-bg-ass-table">{row.usageType}</td>
                                            {summaryTable.headers.slice(1).map((header, colIndex) => (
                                                <td key={colIndex} className="text-xs-1 fw-300">{row[header.key]}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                    <div className="row pt-2">
                        <div className="col-12">
                            <div className="float-right">
                                <a
                                    className="text-blue fw-400"
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleDownload();
                                    }}
                                    style={{
                                        textAlign: "right",
                                        display: "block",
                                        textDecoration: "none"
                                    }}
                                >
                                    使用量内訳をCSVで出力する ≫
                                </a>
                                {facility.data_file_path && (
                                    <a className="text-blue fw-400" style={{ textAlign: "right", display: "block", marginTop: "10px" }}
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleUsageFileDownload(facility.data_file_path, `${facility.name}_30分値データ.csv`);
                                        }}
                                        target="_blank"
                                    >
                                        使用量実績表（30分値データ）をダウンロードする ≫
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>

                    <hr className="dotline" />
                    <div className="row font-weight-normal">
                        <div className="col-5">
                            <span className="me-3">設備情報：</span>
                            {!facility.option_1 && !facility.option_2 && !facility.option_3 && <span>なし</span>}
                            {facility.option_1 ? <span className="bg-ass-demo py-1 px-2 mr-3"> 蓄熱槽 </span> : null}
                            {facility.option_2 ? <span className="bg-ass-demo py-1 px-2 mr-3"> 厨房機器 </span> : null}
                            {facility.option_3 ? <span className="bg-ass-demo py-1 px-2"> 総合電化 </span> : null}
                        </div>
                        <div className="col-7 text-danger fw-bold">
                            <span className="me-3">備考：</span>
                            {facility.note ? facility.note : 'なし'}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default DetailBill;
